import type { OverviewMetricKey, OverviewValues } from "@unbox/api";
import { formatCountCompact, formatMoneyCompact, formatPercentValue, percentChange } from "./format";

export type MetricKey = OverviewMetricKey;

export type MetricGroup = "GMV" | "Samples" | "Videos" | "Creators";

export type ChangeUnit = "percent" | "points";

export type Totals = OverviewValues;

export type MetricDefinition = {
  key: MetricKey;
  label: string;
  group: MetricGroup;
  change: ChangeUnit;
  money: boolean;
  format: (value: number, currency: string) => string;
  value: (totals: Totals) => number | null;
};

const ratio = (numerator: number, denominator: number): number | null =>
  denominator === 0 ? null : numerator / denominator;

const percentOf = (numerator: number, denominator: number): number | null => {
  const value = ratio(numerator, denominator);
  return value === null ? null : value * 100;
};

const decimal = (value: number) => value.toFixed(2);

const percentValue = (value: number) => formatPercentValue(value);

const count = (value: number) => formatCountCompact(value);

export const METRICS: MetricDefinition[] = [
  {
    key: "shopGmv",
    label: "Total shop GMV",
    group: "GMV",
    change: "percent",
    money: true,
    format: formatMoneyCompact,
    value: (t) => t.shopGmv,
  },
  {
    key: "affiliateGmv",
    label: "Affiliate GMV",
    group: "GMV",
    change: "percent",
    money: true,
    format: formatMoneyCompact,
    value: (t) => t.affiliateGmv,
  },
  {
    key: "gmvPerVideo",
    label: "GMV per video",
    group: "GMV",
    change: "percent",
    money: true,
    format: formatMoneyCompact,
    value: (t) => ratio(t.shopGmv, t.videos),
  },
  {
    key: "gmvPerSample",
    label: "GMV per sample",
    group: "GMV",
    change: "percent",
    money: true,
    format: formatMoneyCompact,
    value: (t) => ratio(t.shopGmv, t.samplesApproved),
  },
  {
    key: "sampleRequests",
    label: "Sample requests",
    group: "Samples",
    change: "percent",
    money: false,
    format: count,
    value: (t) => t.sampleRequests,
  },
  {
    key: "samplesApproved",
    label: "Samples approved",
    group: "Samples",
    change: "percent",
    money: false,
    format: count,
    value: (t) => t.samplesApproved,
  },
  {
    key: "videos",
    label: "Videos posted",
    group: "Videos",
    change: "percent",
    money: false,
    format: count,
    value: (t) => t.videos,
  },
  {
    key: "views",
    label: "Video views",
    group: "Videos",
    change: "percent",
    money: false,
    format: count,
    value: (t) => t.views,
  },
  {
    key: "videoConversionRate",
    label: "Video conversion rate",
    group: "Videos",
    change: "points",
    money: false,
    format: percentValue,
    value: (t) => percentOf(t.videoOrders, t.videoClicks),
  },
  {
    key: "gmvDrivingVideos",
    label: "GMV-driving videos",
    group: "Videos",
    change: "percent",
    money: false,
    format: count,
    value: (t) => t.gmvDrivingVideos,
  },
  {
    key: "videosPerCreator",
    label: "Avg videos per creator",
    group: "Videos",
    change: "percent",
    money: false,
    format: decimal,
    value: (t) => ratio(t.videos, t.activeCreators),
  },
  {
    key: "ctr",
    label: "Avg CTR",
    group: "Videos",
    change: "points",
    money: false,
    format: percentValue,
    value: (t) => percentOf(t.clicks, t.views),
  },
  {
    key: "activeCreators",
    label: "Active creators",
    group: "Creators",
    change: "percent",
    money: false,
    format: count,
    value: (t) => t.activeCreators,
  },
];

export const DEFAULT_PINNED_METRICS: MetricKey[] = [
  "affiliateGmv",
  "activeCreators",
  "videos",
  "gmvPerVideo",
  "gmvPerSample",
];

export const metricByKey = (key: MetricKey): MetricDefinition => {
  const metric = METRICS.find((entry) => entry.key === key);
  if (!metric) throw new Error(`Unknown metric ${key}`);
  return metric;
};

export const sumValues = (values: OverviewValues[]): Totals => {
  const totals: Totals = {
    shopGmv: 0,
    affiliateGmv: 0,
    videos: 0,
    views: 0,
    clicks: 0,
    videoClicks: 0,
    videoOrders: 0,
    gmvDrivingVideos: 0,
    activeCreators: 0,
    sampleRequests: 0,
    samplesApproved: 0,
  };
  for (const value of values) {
    totals.shopGmv += value.shopGmv;
    totals.affiliateGmv += value.affiliateGmv;
    totals.videos += value.videos;
    totals.views += value.views;
    totals.clicks += value.clicks;
    totals.videoClicks += value.videoClicks;
    totals.videoOrders += value.videoOrders;
    totals.gmvDrivingVideos += value.gmvDrivingVideos;
    totals.activeCreators += value.activeCreators;
    totals.sampleRequests += value.sampleRequests;
    totals.samplesApproved += value.samplesApproved;
  }
  return totals;
};

export type MetricReading = { value: number | null; change: number | null };

export const readMetric = (metric: MetricDefinition, current: Totals, prior: Totals): MetricReading => {
  const now = metric.value(current);
  const before = metric.value(prior);
  if (metric.change === "points") {
    return { value: now, change: now === null || before === null ? null : now - before };
  }
  return { value: now, change: percentChange(now, before) };
};
